// features/workstations/qqq/today/open-observations-card.js
// ═══════════════════════════════════════════════════════════
// Open Observations Card
// ═══════════════════════════════════════════════════════════
//
// Per docs/QQQ_DASHBOARD_REBUILD_SPEC.md Section II + III.
//
// Migrated and elevated from the original tabs/qqq.js
// `OpenObservationsCard` (line ~4940). Now lives on TODAY tab as a
// primary surface (not buried in a sub-tab) so capture during
// trading is frictionless.
//
// Phase 2b-2: minimal viable. Reads + writes via existing
// /api/workstations/qqq-observations endpoint pattern. If that
// endpoint doesn't exist yet, the card surfaces a placeholder and
// queues writes locally.
//
// Phase 2b-3 will add news-star integration: when a user stars a
// headline in the NewsFeedCard, it auto-injects an observation
// here with timestamp + source + WHISPERER tag if applicable.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';

const ENDPOINT = '/api/workstations/qqq-observations';

export default function OpenObservationsCard({ workstation, userId }) {
  const [observations, setObservations] = useState([]);
  const [text, setText] = useState('');
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!workstation?.id || !userId) {
        setObservations([]);
        setInfo('Workstation context required to load observations.');
        return;
      }
      const params = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
        limit: '20',
      });
      const res = await fetch(`${ENDPOINT}?${params}`, { cache: 'no-store' });
      if (res.status === 404) {
        // Endpoint not yet wired in this rebuild path; show placeholder
        setInfo('Observations endpoint not wired in Phase 2b-2 path; observations land here once the endpoint is available. The capture form below queues locally for now.');
        setObservations([]);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setObservations(json.observations || json.items || []);
      setInfo(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);
      if (!workstation?.id || !userId) {
        // Local queue fallback (Phase 2b-2 placeholder)
        const local = {
          id: `local-${Date.now()}`,
          observation: text.trim(),
          tags: tagsArray,
          created_at: new Date().toISOString(),
          source: 'local_queue',
        };
        setObservations(prev => [local, ...prev]);
        setText(''); setTags('');
        setInfo('Saved locally. Will persist once observations endpoint is available.');
        return;
      }
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workstation_id: workstation.id,
          user_id: userId,
          observation: text.trim(),
          tags: tagsArray,
        }),
      });
      if (res.ok) {
        await load();
        setText(''); setTags('');
        setInfo(null);
      } else if (res.status === 404) {
        // Fallback to local queue
        const local = {
          id: `local-${Date.now()}`,
          observation: text.trim(),
          tags: tagsArray,
          created_at: new Date().toISOString(),
          source: 'local_queue',
        };
        setObservations(prev => [local, ...prev]);
        setText(''); setTags('');
        setInfo('Endpoint missing — queued locally.');
      } else {
        throw new Error(`HTTP ${res.status}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="qqq-observations-card">
      <div className="qqq-observations-card__header">
        <div className="qqq-observations-card__title">Open Observations</div>
        <button type="button" onClick={load} className="qqq-observations-card__refresh-btn">Refresh</button>
      </div>

      {/* Capture form */}
      <div className="qqq-observations-card__form">
        <textarea
          className="qqq-observations-card__textarea"
          rows={3}
          placeholder="Capture an observation while trading… patterns, hypotheses, anomalies"
          value={text}
          onChange={e => setText(e.target.value)}
        />
        <input
          className="qqq-observations-card__tags-input"
          type="text"
          placeholder="Tags (comma-separated): event-day, vehicle, retest"
          value={tags}
          onChange={e => setTags(e.target.value)}
        />
        <button
          type="button"
          className="qqq-observations-card__submit"
          onClick={submit}
          disabled={submitting || !text.trim()}
        >
          {submitting ? 'Saving…' : 'Save observation'}
        </button>
      </div>

      {info && <div className="qqq-observations-card__info">{info}</div>}
      {error && <div className="qqq-observations-card__error">Error: {error}</div>}

      {/* Recent observations */}
      <div className="qqq-observations-card__list">
        {loading && observations.length === 0 && (
          <div className="qqq-observations-card__loading">Loading…</div>
        )}
        {!loading && observations.length === 0 && (
          <div className="qqq-observations-card__empty">No observations yet.</div>
        )}
        {observations.map(obs => (
          <div key={obs.id} className="qqq-observations-card__item">
            <div className="qqq-observations-card__item-text">
              {obs.observation || obs.text || obs.note || ''}
            </div>
            <div className="qqq-observations-card__item-meta">
              <span className="qqq-observations-card__item-date">
                {obs.created_at ? formatRelative(obs.created_at) : ''}
              </span>
              {Array.isArray(obs.tags) && obs.tags.length > 0 && (
                <div className="qqq-observations-card__item-tags">
                  {obs.tags.map(t => (
                    <span key={t} className="qqq-observations-card__item-tag">{t}</span>
                  ))}
                </div>
              )}
              {obs.source && obs.source !== 'manual' && (
                <span className="qqq-observations-card__item-source">{obs.source}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatRelative(iso) {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
